import { 
    CognitoIdentityProviderClient,
    InitiateAuthCommand,
    SignUpCommand,
    ConfirmSignUpCommand,
    ForgotPasswordCommand,
    ConfirmForgotPasswordCommand,
    ChangePasswordCommand,
    DeleteUserCommand,
    RespondToAuthChallengeCommand,
    GetUserCommand
} from "@aws-sdk/client-cognito-identity-provider";
import { cognitoConfig } from '../config/cognitoConfig';
import { jwtDecode } from 'jwt-decode';

const client = new CognitoIdentityProviderClient({
    region: cognitoConfig.region
});

// Token management
const TOKEN_STORAGE_KEY = {
    ACCESS: 'accessToken',
    ID: 'idToken',
    REFRESH: 'refreshToken'
};

// Decode JWT token
const decodeToken = (token) => {
    try {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(atob(base64).split('').map(c => {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        }).join(''));
        return JSON.parse(jsonPayload);
    } catch (error) {
        console.error('Error decoding token:', error);
        return null;
    }
};

export const storeTokens = (tokens) => {
    localStorage.setItem(TOKEN_STORAGE_KEY.ACCESS, tokens.AccessToken);
    localStorage.setItem(TOKEN_STORAGE_KEY.ID, tokens.IdToken);
    localStorage.setItem(TOKEN_STORAGE_KEY.REFRESH, tokens.RefreshToken);
    
    // Decode and log ID token contents
    try {
        const decodedIdToken = jwtDecode(tokens.IdToken);
        console.log('Decoded ID Token:', {
            sub: decodedIdToken.sub,
            email: decodedIdToken.email,
            name: decodedIdToken.name,
            'cognito:groups': decodedIdToken['cognito:groups'],
            token_use: decodedIdToken.token_use,
            exp: new Date(decodedIdToken.exp * 1000).toLocaleString(),
            iat: new Date(decodedIdToken.iat * 1000).toLocaleString(),
            auth_time: new Date(decodedIdToken.auth_time * 1000).toLocaleString()
        });
    } catch (error) {
        console.error('Error decoding ID token:', error);
    }
};

export const clearTokens = () => {
    localStorage.removeItem(TOKEN_STORAGE_KEY.ACCESS);
    localStorage.removeItem(TOKEN_STORAGE_KEY.ID);
    localStorage.removeItem(TOKEN_STORAGE_KEY.REFRESH);
};

export const getStoredTokens = () => ({
    accessToken: localStorage.getItem(TOKEN_STORAGE_KEY.ACCESS),
    idToken: localStorage.getItem(TOKEN_STORAGE_KEY.ID),
    refreshToken: localStorage.getItem(TOKEN_STORAGE_KEY.REFRESH)
});

// Auth operations
export const signUp = async (email, password, name) => {
    const command = new SignUpCommand({
        ClientId: cognitoConfig.clientId,
        Username: email,
        Password: password,
        UserAttributes: [
            {
                Name: 'email',
                Value: email
            },
            {
                Name: 'name',
                Value: name
            }
        ]
    });

    try {
        const response = await client.send(command);
        return response;
    } catch (error) {
        console.error('Sign up error:', error);
        throw error;
    }
};

export const confirmSignUp = async (email, code) => {
    const command = new ConfirmSignUpCommand({
        ClientId: cognitoConfig.clientId,
        Username: email,
        ConfirmationCode: code
    });

    try {
        const response = await client.send(command);
        return response;
    } catch (error) {
        console.error('Confirm sign up error:', error);
        throw error;
    }
};

export const signIn = async (email, password) => {
    const command = new InitiateAuthCommand({
        AuthFlow: 'USER_PASSWORD_AUTH',
        ClientId: cognitoConfig.clientId,
        AuthParameters: {
            USERNAME: email,
            PASSWORD: password
        }
    });

    try {
        const response = await client.send(command);
        
        if (response.ChallengeName === 'NEW_PASSWORD_REQUIRED') {
            return { challengeName: 'NEW_PASSWORD_REQUIRED', session: response.Session };
        }

        if (response.AuthenticationResult) {
            storeTokens(response.AuthenticationResult);
        }

        return response;
    } catch (error) {
        console.error('Sign in error:', error);
        throw error;
    }
};

export const completeNewPassword = async (email, newPassword, session) => {
    const command = new RespondToAuthChallengeCommand({
        ClientId: cognitoConfig.clientId,
        ChallengeName: 'NEW_PASSWORD_REQUIRED',
        Session: session,
        ChallengeResponses: {
            USERNAME: email,
            NEW_PASSWORD: newPassword
        }
    });

    try {
        const response = await client.send(command);
        if (response.AuthenticationResult) {
            storeTokens(response.AuthenticationResult);
        }
        return response;
    } catch (error) {
        console.error('Complete new password error:', error);
        throw error;
    }
};

export const forgotPassword = async (email) => {
    const command = new ForgotPasswordCommand({
        ClientId: cognitoConfig.clientId,
        Username: email
    });

    try {
        const response = await client.send(command);
        return response;
    } catch (error) {
        console.error('Forgot password error:', error);
        throw error;
    }
};

export const confirmForgotPassword = async (email, code, newPassword) => {
    const command = new ConfirmForgotPasswordCommand({
        ClientId: cognitoConfig.clientId,
        Username: email,
        ConfirmationCode: code,
        Password: newPassword
    });

    try {
        const response = await client.send(command);
        return response;
    } catch (error) {
        console.error('Confirm forgot password error:', error);
        throw error;
    }
};

export const changePassword = async (oldPassword, newPassword) => {
    const { accessToken } = getStoredTokens();
    
    const command = new ChangePasswordCommand({
        AccessToken: accessToken,
        PreviousPassword: oldPassword,
        ProposedPassword: newPassword
    });

    try {
        const response = await client.send(command);
        return response;
    } catch (error) {
        console.error('Change password error:', error);
        throw error;
    }
};

export const deleteAccount = async () => {
    const { accessToken } = getStoredTokens();
    
    const command = new DeleteUserCommand({
        AccessToken: accessToken
    });

    try {
        const response = await client.send(command);
        clearTokens();
        return response;
    } catch (error) {
        console.error('Delete account error:', error);
        throw error;
    }
};

export const refreshSession = async () => {
    const { refreshToken } = getStoredTokens();
    
    const command = new InitiateAuthCommand({
        AuthFlow: 'REFRESH_TOKEN_AUTH',
        ClientId: cognitoConfig.clientId,
        AuthParameters: {
            REFRESH_TOKEN: refreshToken
        }
    });

    try {
        const response = await client.send(command);
        if (response.AuthenticationResult) {
            // Store new tokens but keep existing refresh token
            localStorage.setItem(TOKEN_STORAGE_KEY.ACCESS, response.AuthenticationResult.AccessToken);
            localStorage.setItem(TOKEN_STORAGE_KEY.ID, response.AuthenticationResult.IdToken);
        }
        return response;
    } catch (error) {
        console.error('Refresh session error:', error);
    // Do not clear tokens here; let callers decide how to handle.
    // Returning a rejected promise preserves existing tokens in case of transient errors.
    throw error;
    }
};

export const getCurrentUser = async () => {
    const { accessToken } = getStoredTokens();
    
    const command = new GetUserCommand({
        AccessToken: accessToken
    });

    try {
        const response = await client.send(command);
        return response;
    } catch (error) {
        console.error('Get user error:', error);
        throw error;
    }
}; 